/**
 * src/lib/voice/conversationStreaming.js — T3 conversation streaming-STT controller
 *
 * Spec: docs/plans/2026-06-06-streaming-stt-endpoint-spec.md §3 (seam) + §6 T3.
 *
 * Thin orchestration layer between useWhisperRecording's conversation path and
 * the T2 streaming client (createStreamingStt). Extracted out of the 838-line
 * hook to keep it under the project's 500-line split ceiling — the hook holds
 * ONE of these in a ref and drives it through the start/stop lifecycle.
 *
 * Lifecycle:
 *   1. startConversationStreamer({...}) — constructs the T2 streamer, wires
 *      onPartial -> consumer, onFinal -> resolve the pending "final" promise,
 *      onError -> mark error + resolve the pending promise, then calls
 *      streamer.start() INSIDE the user gesture (resumes the AudioContext).
 *      Returns a controller, OR null if streaming could not start (caller then
 *      uses its existing MediaRecorder + UVTP fallback unchanged).
 *   2. controller.commit() — at stop time, request the final transcript.
 *   3. controller.awaitFinal(deadlineMs) — resolves to
 *      { text, error } where error is truthy on stream-error/timeout. The
 *      caller decides streaming-final-vs-UVTP-fallback off this result.
 *   4. controller.stop() — releases the streamer's own mic + socket.
 *
 * Observability: start failures emit stt_stream.connect/error on the spine
 * (the T2 client already emits connect/first_partial/final/fallback for the
 * live path); the caller emits stt_stream.fallback when it routes to UVTP.
 */

import { createStreamingStt } from './streamingStt';
import { logVoiceEvent } from './voiceLogger';

/**
 * Construct + start a conversation streaming-STT controller inside the user
 * gesture. Never throws — on any failure it emits a spine error and returns
 * null so the caller transparently keeps the MediaRecorder/UVTP fallback path.
 *
 * @param {object} args
 * @param {string} args.proxyUrl   wss://<box>/stt-stream (non-empty)
 * @param {string} [args.traceId]  turn trace id (threaded to the spine)
 * @param {string} [args.language] 'en' | 'es' language hint
 * @param {string} [args.model]    STT model name from the proxy allowlist
 *                                 (e.g. 'gpt-4o-transcribe'); omit to use
 *                                 the proxy's env default.
 * @param {(text:string)=>void} [args.onPartial] live interim text consumer
 * @param {(opts:object)=>{start:Function,stop:Function,commit:Function,isActive:Function}} [args.factory]
 *        injectable for tests; defaults to createStreamingStt
 * @returns {Promise<null | {
 *   commit():void,
 *   awaitFinal(deadlineMs:number):Promise<{text:string|null,error:Error|null}>,
 *   stop():void,
 *   isActive():boolean,
 * }>}
 */
export async function startConversationStreamer({
  proxyUrl,
  traceId = null,
  language = 'en',
  model = null,
  onPartial,
  factory = createStreamingStt,
} = {}) {
  let finalText = null;
  let streamError = null;
  let settled = false;
  let resolvePending = null;
  const finalPromise = new Promise((resolve) => { resolvePending = resolve; });
  // TICKET-074 — streaming STT emits DELTAS (incremental fragments from
  // input_audio_transcription.delta), NOT the full text. The downstream
  // consumer (useVoiceConversation.mergePartial) stores streamed partials in a
  // single slot at the -1 sentinel index and OVERWRITES, so handing it raw
  // deltas collapses the live transcript to just the latest fragment ("only the
  // last word"). Accumulate the deltas here so the consumer always receives the
  // cumulative interim — the whole sentence forming. This closure is fresh per
  // startConversationStreamer call (one per turn), and we also reset on final so
  // a reused controller never bleeds the prior utterance into the next.
  let interim = '';

  const settle = () => {
    if (settled) return;
    settled = true;
    if (resolvePending) resolvePending();
  };

  const streamer = factory({
    proxyUrl,
    trace_id: traceId,
    language,
    model: model || undefined,
    onPartial: (text) => {
      // TICKET-074 — accumulate the delta into the cumulative interim (deltas
      // carry their own leading spaces, so plain concatenation faithfully
      // rebuilds the text exactly as OpenAI's own completed transcript would).
      // TICKET-VOICE-FINAL-DEADLOCK — accumulate UNCONDITIONALLY (even with no
      // UI consumer): the interim is now a transcript source — the seam uses
      // getInterim() to salvage the turn when the streamed final never lands.
      if (typeof text === 'string') interim += text;
      // Partials must NEVER throw out of the audio/WS callback. chunkIndex
      // sentinel -1 marks "streamed partial" (not a MediaRecorder chunk index).
      if (!onPartial) return;
      try { onPartial(interim, -1); } catch {
        // spine-emit-ok: consumer onPartial threw — non-fatal live-text UI callback
      }
    },
    onFinal: (text) => {
      finalText = typeof text === 'string' ? text : '';
      // TICKET-074 — clear the interim so a reused controller starts the next
      // utterance from empty (the final transcript supersedes the interim).
      interim = '';
      settle();
    },
    onError: (err) => {
      streamError = err instanceof Error ? err : new Error(String(err?.message ?? err ?? 'stream_error'));
      settle();
    },
  });

  let active = false;
  try {
    await streamer.start();
    active = true;
  } catch (err) {
    logVoiceEvent('stt_stream.connect', 'error', err?.message || 'streaming start failed', {
      trace_id: traceId,
      error_code: 'start_failed',
    });
    try { streamer.stop(); } catch {
      // spine-emit-ok: stop on a never-started streamer is benign cleanup
    }
    return null;
  }

  return {
    commit() { streamer.commit(); },
    /**
     * TICKET-VOICE-FINAL-DEADLOCK — the cumulative interim accumulated from
     * deltas this turn ('' once the real final arrived, which supersedes it).
     * On final-timeout the seam uses this as the transcript: it is exactly the
     * text the user already saw live, and strictly better than dropping the
     * turn into the UVTP fallback.
     */
    getInterim() { return interim; },
    /**
     * Wait for the streamed final (or stream error) up to deadlineMs.
     * Resolves { text, error } — text is the final on success, error is set on
     * stream-error or when the deadline elapses with no final.
     */
    async awaitFinal(deadlineMs) {
      let timer = null;
      const timeout = new Promise((resolve) => {
        timer = setTimeout(resolve, deadlineMs);
      });
      await Promise.race([finalPromise, timeout]);
      if (timer) clearTimeout(timer);
      if (streamError) return { text: null, error: streamError };
      if (settled) return { text: finalText, error: null };
      // Deadline elapsed before any final/error arrived.
      return { text: null, error: new Error('streaming final deadline elapsed') };
    },
    stop() {
      active = false;
      try { streamer.stop(); } catch {
        // spine-emit-ok: stop is idempotent best-effort socket/mic release
      }
    },
    isActive() { return active && (typeof streamer.isActive === 'function' ? streamer.isActive() : true); },
  };
}
